import type { Product } from '@clockwise/clients/products';
import { ApiError } from '@clockwise/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { RowActions } from '@/components/custom/row-actions';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { customerActivitiesApi, serviceRolesApi } from '@/lib/api';
import { isForbidden } from '@/lib/errors';
import { queryKeys } from '@/lib/query-keys';
import { CustomerActivityFormSheet } from './customer-activity-form-sheet';
import { LifecycleStatusBadge } from './lifecycle-status-badge';

interface Props {
  customerId: string;
  /** The owning customer's internal flag. A custom activity's "internal" is
   *  always its customer's, so the form hides the toggle and uses this. */
  isInternal: boolean;
  /** Whether the caller may manage this customer's activities — the detail
   *  response's `related.actions.editActivities` (customer edit on THIS
   *  customer, plus `activity.edit`), computed by the API's own checks. */
  canEdit: boolean;
}

const SERVICE_ROLES_LIST_QUERY = {
  sortField: 'name',
  sortDirection: 'asc',
  limit: 200,
} as const;

type SheetState = { mode: 'add' } | { mode: 'edit'; activity: Product } | null;

/**
 * Customer-specific activities for one customer — the customer-detail
 * "Activities" tab. A custom activity is an ordinary product pinned to this
 * customer; it shows alongside the global catalog in this customer's timesheet
 * activity picker and nowhere else. Managing it requires BOTH `customer.edit`
 * on this customer and `activity.edit`; the page passes the API's answer.
 */
export function CustomerActivitiesPanel({ customerId, isInternal, canEdit }: Props) {
  const queryClient = useQueryClient();

  const [sheet, setSheet] = useState<SheetState>(null);

  const activitiesQuery = useQuery({
    queryKey: queryKeys.customerActivities.list(customerId),
    queryFn: () => customerActivitiesApi.list(customerId),
  });
  const activities = activitiesQuery.data?.data ?? [];

  // Resolve service-role id → short code for the Role column. Cached and shared
  // with the form sheet's role select.
  const rolesQuery = useQuery({
    queryKey: queryKeys.serviceRoles.list(SERVICE_ROLES_LIST_QUERY),
    queryFn: () => serviceRolesApi.list(SERVICE_ROLES_LIST_QUERY),
  });
  const roleShortCodeById = useMemo(
    () => new Map((rolesQuery.data?.data ?? []).map((r) => [r.id, r.shortCode])),
    [rolesQuery.data],
  );

  const deleteMutation = useMutation({
    mutationFn: (id: string) => customerActivitiesApi.delete(customerId, id),
    onSuccess: () => {
      toast.success('Activity removed');
      queryClient.invalidateQueries({ queryKey: queryKeys.customerActivities.list(customerId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.products.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.audit.all });
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Failed to remove activity'),
  });

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-xs text-muted-foreground">
          {activitiesQuery.isFetching
            ? 'Loading…'
            : `${activities.length} activit${activities.length === 1 ? 'y' : 'ies'}`}
        </span>
        <div className="ml-auto">
          {canEdit ? (
            <Button size="sm" onClick={() => setSheet({ mode: 'add' })}>
              <Plus />
              Add activity
            </Button>
          ) : null}
        </div>
      </div>

      <p className="text-xs text-muted-foreground">
        Activities defined here appear only for this customer, alongside the standard activities, in
        the timesheet activity picker.
      </p>

      <div className="rounded-md border">
        {activitiesQuery.isLoading ? (
          <Skeleton className="h-32 w-full" />
        ) : activitiesQuery.isError ? (
          isForbidden(activitiesQuery.error) ? (
            <p className="p-6 text-center text-sm text-muted-foreground">
              You don’t have access to this customer’s activities.
            </p>
          ) : (
            <p className="p-4 text-sm text-destructive">
              {activitiesQuery.error instanceof ApiError
                ? activitiesQuery.error.message
                : 'Failed to load activities.'}
            </p>
          )
        ) : activities.length === 0 ? (
          <p className="p-6 text-center text-sm text-muted-foreground">
            No customer-specific activities yet. Click “Add activity”.
          </p>
        ) : (
          <Table>
            {/* `bg-surface-subtle` — the same cream header tint every other
                list table uses (see `sticky-table-scroll.tsx`); this table
                isn't wrapped in that component (it scrolls with the page,
                not internally), so the tint has to be stated here instead. */}
            <TableHeader className="bg-surface-subtle">
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Retainer code</TableHead>
                <TableHead>T&amp;M code</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-24 text-right" aria-label="actions" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {activities.map((a) => (
                <TableRow key={a.id}>
                  <TableCell>
                    <span className="font-medium">{a.name}</span>
                    {a.internal ? (
                      <span className="ml-2 rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
                        Internal
                      </span>
                    ) : null}
                  </TableCell>
                  <TableCell className="text-sm">
                    {roleShortCodeById.get(a.serviceRoleId) ?? (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-sm">{a.retainerCode || '—'}</TableCell>
                  <TableCell className="text-sm">{a.tmCode || '—'}</TableCell>
                  <TableCell>
                    <LifecycleStatusBadge status={a.status} />
                  </TableCell>
                  <TableCell className="text-right">
                    {canEdit ? (
                      <RowActions>
                        <Button
                          size="icon-sm"
                          variant="ghost"
                          aria-label="Edit"
                          onClick={() => setSheet({ mode: 'edit', activity: a })}
                        >
                          <Pencil />
                        </Button>
                        <Button
                          size="icon-sm"
                          variant="ghost"
                          aria-label="Remove activity"
                          onClick={() => {
                            if (confirm(`Remove “${a.name}”?`)) deleteMutation.mutate(a.id);
                          }}
                          disabled={deleteMutation.isPending}
                        >
                          <Trash2 />
                        </Button>
                      </RowActions>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <CustomerActivityFormSheet
        open={sheet?.mode === 'add' || sheet?.mode === 'edit'}
        onOpenChange={(o) => {
          if (!o) setSheet(null);
        }}
        customerId={customerId}
        customerIsInternal={isInternal}
        activity={sheet?.mode === 'edit' ? sheet.activity : null}
      />
    </div>
  );
}
